import { useEffect, useRef, useState, useCallback } from 'react';
import { getWebSocketToken } from '@/api/auth';
import { fetchChatHistory } from '@/api/chat';
import { useCurrentUserModule } from './useUserModule';
import { useNoGuard } from './useNoGuard';
import { mockChatHistory, getMockAIResponse } from '@/mocks/mockChatMessages';

export interface ChatMessage {
  id?: number;
  text: string;
  fromUser: boolean;
  timestamp: string;
  userModuleId?: number;
}

interface UseWebSocketChatProps {
  onMessageReceived?: (message: ChatMessage) => void;
}

export function useWebSocketChat({ onMessageReceived }: UseWebSocketChatProps = {}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentResponse, setCurrentResponse] = useState('');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentResponseRef = useRef<string>('');
  const { data: currentModule } = useCurrentUserModule();
  const isNoGuard = useNoGuard();

  // Load chat history on mount
  useEffect(() => {
    const loadHistory = async () => {
      // In noguard mode, use mock data
      if (isNoGuard) {
        setMessages(mockChatHistory);
        setIsConnected(true);
        setIsLoading(false);
        return;
      }
      
      try {
        const history = await fetchChatHistory();
        // Transform the history to match our interface
        console.log('Raw chat history from backend:', history);
        
        const transformedHistory = history.map((msg: ChatMessage) => ({
          id: msg.id,
          text: msg.text,
          fromUser: msg.fromUser,
          timestamp: msg.timestamp,
          userModuleId: msg.userModuleId
        }));
        
        console.log('Transformed history before reverse:', transformedHistory);
        
        // Check if we have history
        if (transformedHistory.length === 0) {
          // No history - this shouldn't happen if backend is working correctly
          // But we'll add a fallback welcome message just in case
          const welcomeMessage: ChatMessage = {
            text: "Hello! I'm your AI coach, and I'm here to help you navigate change and develop your leadership skills. Based on your PULSE survey results, I can see you're ready to make positive changes. What would you like to explore today?",
            fromUser: false,
            timestamp: new Date().toISOString(),
            userModuleId: currentModule?.id
          };
          setMessages([welcomeMessage]);
        } else {
          // Show existing history (backend should have added initial message)
          const reversedHistory = transformedHistory.reverse(); // Reverse to show oldest first
          console.log('History after reverse (should show oldest first):', reversedHistory);
          setMessages(reversedHistory);
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
        // Even on error, show a welcome message
        const welcomeMessage: ChatMessage = {
          text: "Hello! I'm your AI coach, and I'm here to help you navigate change and develop your leadership skills. What challenges or opportunities would you like to discuss today?",
          fromUser: false,
          timestamp: new Date().toISOString(),
          userModuleId: currentModule?.id
        };
        setMessages([welcomeMessage]);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [currentModule?.id, isNoGuard]);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    try {
      // Get WebSocket token
      const token = await getWebSocketToken();
      
      // Construct WebSocket URL
      const wsBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('http', 'ws') || 'ws://localhost:8000';
      const userModuleId = currentModule?.id;
      const wsUrl = `${wsBaseUrl}/api/chat/ws/chat?token=${token}${userModuleId ? `&user_module_id=${userModuleId}` : ''}`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      
      // Create WebSocket connection
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        // Clear any pending reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'partial') {
            // Streaming response - accumulate the text
            setCurrentResponse(prev => {
              const newResponse = prev + data.text;
              currentResponseRef.current = newResponse;
              return newResponse;
            });
          } else if (data.type === 'done') {
            // Complete response - use the accumulated text from currentResponse
            // The backend sends the full text, but we've been showing it progressively
            const finalText = currentResponseRef.current || data.text;
            if (finalText) {
              const completeMessage: ChatMessage = {
                text: finalText,
                fromUser: false,
                timestamp: new Date().toISOString(),
                userModuleId: currentModule?.id
              };
              
              setMessages(prev => [...prev, completeMessage]);
              
              if (onMessageReceived) {
                onMessageReceived(completeMessage);
              }
            }
            // Always clear the currentResponse when done
            setCurrentResponse('');
            currentResponseRef.current = '';
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        wsRef.current = null;
        
        // Attempt to reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect...');
          connect();
        }, 3000);
      };

    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      setIsConnected(false);
      
      // Retry connection after 5 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, 5000);
    }
  }, [currentModule?.id, onMessageReceived]);

  // Connect on mount
  useEffect(() => {
    // Skip WebSocket connection in noguard mode
    if (!isLoading && !isNoGuard) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connect, isLoading, isNoGuard]);

  // Send message function
  const sendMessage = useCallback((text: string) => {
    // In noguard mode, simulate chat
    if (isNoGuard) {
      // Add user message
      const userMessage: ChatMessage = {
        id: Date.now(),
        text,
        fromUser: true,
        timestamp: new Date().toISOString(),
        userModuleId: currentModule?.id
      };
      setMessages(prev => [...prev, userMessage]);
      
      // Simulate AI response with typing effect
      setCurrentResponse('');
      currentResponseRef.current = '';
      
      setTimeout(() => {
        const aiResponse = getMockAIResponse(text);
        let index = 0;
        
        const typeInterval = setInterval(() => {
          if (index < aiResponse.length) {
            const chunk = aiResponse.substring(index, Math.min(index + 5, aiResponse.length));
            currentResponseRef.current += chunk;
            setCurrentResponse(currentResponseRef.current);
            index += 5;
          } else {
            clearInterval(typeInterval);
            // Add complete message
            const aiMessage: ChatMessage = {
              id: Date.now() + 1,
              text: aiResponse,
              fromUser: false,
              timestamp: new Date().toISOString(),
              userModuleId: currentModule?.id
            };
            setMessages(prev => [...prev, aiMessage]);
            setCurrentResponse('');
            currentResponseRef.current = '';
          }
        }, 30);
      }, 500);
      
      return;
    }
    
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('Not connected: Please wait for the connection to be established');
      return;
    }

    // Add user message to the UI immediately
    const userMessage: ChatMessage = {
      text,
      fromUser: true,
      timestamp: new Date().toISOString(),
      userModuleId: currentModule?.id
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    // Send message through WebSocket
    try {
      wsRef.current.send(JSON.stringify({ text }));
    } catch (error) {
      console.error('Failed to send message:', error);
      console.error('Send failed: Failed to send your message.');
    }
  }, [currentModule?.id, isNoGuard]);

  return {
    messages,
    sendMessage,
    isConnected,
    isLoading,
    currentResponse,
    hasActiveResponse: currentResponse.length > 0
  };
}